import { IUserProfile } from '@account-users/models/IUserProfile';
import { IUserRole } from '@account-users/user/models/UserRole';
import { Permission } from '@account-users/account/models/Permission';
import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as auth0 from 'auth0-js';
import { differenceInMinutes } from 'date-fns';
import { BehaviorSubject, Observable } from 'rxjs';

import { SilentSsoLoginService } from './silent-sso-login.service';

(window as any).global = window;

// this is a workaround for auth0-js typings missing this property, while actually supporting it in runtime
interface AuthOptions extends auth0.AuthOptions {
  overrides?: {
    __tenant: string;
    __token_issuer: string;
  };
}

interface DecodedAccessToken {
  exp: number;
}

interface Auth0EnvConfig {
  clientID: string;
  domain: string;
  audience: string;
  issuer: string;
  tenant: string;
}

@Injectable()
export class AuthService {
  private readonly auth0: auth0.WebAuth;
  private readonly jwtHelperService = new JwtHelperService();
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this._isAuthenticated$.asObservable();

  constructor(
    private readonly silentSSOLoginService: SilentSsoLoginService,
    public router: Router,
    @Inject('auth0') private auth0Config: Auth0EnvConfig,
    @Inject('siteUrl') private siteUrl: string,
    @Inject('avatarBaseUrl') private avatarBaseUrl: string,
  ) {
    const authOptions: AuthOptions = {
      clientID: this.auth0Config ? this.auth0Config.clientID : '',
      domain: this.auth0Config ? this.auth0Config.domain : '',
      responseType: 'token id_token',
      audience: this.auth0Config ? this.auth0Config.audience : '',
      redirectUri: `${this.siteUrl}callback`,
      scope: 'openid email',
    };

    if (this.auth0Config && !this.auth0Config.domain.endsWith('auth0.com')) {
      authOptions.overrides = {
        __tenant: this.auth0Config ? this.auth0Config.tenant : '',
        __token_issuer: this.auth0Config ? this.auth0Config.issuer : '',
      };
    }

    this.auth0 = new auth0.WebAuth(authOptions);
  }

  public login(): void {
    this.removeTokens();
    this.auth0.authorize({
      scope: 'login',
      login_hint: this.silentSSOLoginService.userEmail,
    });
  }

  public handleAuthentication(): void {
    this.silentSSOLoginService.setUserEmailFromQueryString();
    this.silentSSOLoginService.setAccountIdFromQueryString();

    this.auth0.parseHash(async (err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        this.setSession(authResult);
        const profile = this.profile;

        if (profile.permissions.includes(Permission.CanLogin)) {
          if (
            this.silentSSOLoginService.userEmail &&
            this.silentSSOLoginService.accountId
          ) {
            this.router.navigate([
              `/au/accounts/${this.silentSSOLoginService.accountId}`,
            ]);
          } else {
            this.router.navigate(['/']);
          }
          this.silentSSOLoginService.clear();
        } else {
          alert('not a support user');
          this.logout();
        }
      } else if (err) {
        console.error(err);
        alert(`Error: ${err.error}. Check the console for further details.`);
        this.logout();
      }
      this._isAuthenticated$.next(this.isAuthenticated());
    });
  }

  public renewTokens() {
    return new Observable((observer) => {
      this.auth0.checkSession({}, (err, authResult: auth0.Auth0DecodedHash) => {
        if (err) {
          observer.error(err);
        } else {
          console.info('Tokens has been renewed');
          observer.next(authResult);
          observer.complete();
        }
      });
    });
  }

  public get profile(): IUserProfile {
    if (this.idToken) {
      return this.parseUserInfo(this.decodeToken(this.idToken));
    }
  }

  public setSession(authResult: auth0.Auth0DecodedHash): void {
    localStorage.setItem('access_token', authResult.accessToken);
    localStorage.setItem('id_token', authResult.idToken);
  }

  public logout(): void {
    this.silentSSOLoginService.clear();
    // Remove tokens and expiry time from localStorage
    this.removeTokens();
    // Redirect to auth0 logout endpoint.
    this.auth0.logout({ returnTo: this.siteUrl });
  }

  public isAuthenticated(): boolean {
    return !this.jwtHelperService.isTokenExpired(this.accessToken);
  }

  get accessToken() {
    return localStorage.getItem('access_token');
  }

  hasRole(role: IUserRole) {
    const profile: IUserProfile = this.profile;
    return profile && profile.roles && profile.roles.includes(role);
  }

  hasPermission(permission: Permission) {
    const profile: IUserProfile = this.profile;
    return (
      profile && profile.permissions && profile.permissions.includes(permission)
    );
  }

  private get idToken() {
    return localStorage.getItem('id_token');
  }

  private decodeToken(token: string) {
    if (!token) {
      throw new Error('invalid token');
    }
    return this.jwtHelperService.decodeToken(token);
  }

  private parseUserInfo(userInfo: any): IUserProfile {
    const getPictureUrl = (pictureString: string) =>
      pictureString.startsWith('http')
        ? pictureString
        : `${this.avatarBaseUrl}${pictureString}`;

    return {
      name: userInfo['http://apsis.one/name'],
      email: userInfo['http://apsis.one/email'],
      picture: getPictureUrl(userInfo['http://apsis.one/picture']),
      roles: userInfo['http://apsis.one/roles'] || [],
      userId: userInfo.sub,
      permissions: userInfo['http://apsis.one/permissions'] || [],
    };
  }

  private removeTokens(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
  }

  public isTokenAboutToExpire() {
    const decoded: DecodedAccessToken = this.decodeToken(this.accessToken);
    const expire = decoded.exp;
    return differenceInMinutes(new Date(expire * 1000), new Date()) < 30;
  }
}
